import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, RootState } from '../../../../store/store';
import { extractChannelNumberFromId } from '../../../../utils/channel-utils';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { IChannelName } from '../../../../types/channel-types';
import EmptyDataTable from '../../../../components/EmptyDataTable/EmptyDataTable';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { ElementSize } from '../../../../types/element-size';
import { ButtonType } from '../../../../types/button-type';
import { ChangeChannelAction } from '../../../../enums/action';
import { changeChannelAction } from '../EditChannel/channelActions';
import { CHANNEL_ACTIONS_BY_ROLE } from '../../../../constants/channel-allowed-actions';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type FaultRow = {
  channelNumber: number;
  channelName: string;
  message: string;
};

const getChannelNumber = (channel: IChannelName): number => {
  const fromId = extractChannelNumberFromId(channel.id);
  if (fromId !== null) {
    return fromId;
  }

  const match = channel.channel?.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

const ChannelFaults: React.FC<ComponentProps> = ({ user, getChannels, getListChannelFaults }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [faultRows, setFaultRows] = useState<FaultRow[]>([]);
  const [clearingChannel, setClearingChannel] = useState<number | null>(null);

  const canClearFaults = useMemo(() => {
    const allowedActions = user?.memberData?.role ? CHANNEL_ACTIONS_BY_ROLE[user.memberData.role] : [];
    return !!allowedActions?.includes(ChangeChannelAction.RESET_CHANNEL_FAULTS);
  }, [user]);

  const fetchData = useCallback(async () => {
    const channelList = (await getChannels(id)) as IChannelName[] | undefined;

    if (!channelList?.length) {
      setFaultRows([]);
      setLoading(false);
      return;
    }

    const result = await getListChannelFaults({ channel_ids: channelList.map((channel) => channel.id) });
    const faultsByChannelId = (result?.channelsFaults || []).reduce<Record<string, string[]>>((acc, item) => {
      acc[item.pk] = item.messages?.value || [];
      return acc;
    }, {});

    const rows: FaultRow[] = channelList
      .map((channel) => ({
        channelNumber: getChannelNumber(channel),
        channelName: channel.name?.trim() || channel.channel,
        messages: faultsByChannelId[channel.id] || [],
      }))
      .filter((item) => item.messages.length > 0)
      .sort((a, b) => a.channelNumber - b.channelNumber)
      .flatMap((item) =>
        item.messages.map((message) => ({
          channelNumber: item.channelNumber,
          channelName: item.channelName,
          message,
        })),
      );

    setFaultRows(rows);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, EXTENDED_INTERVAL_UPDATE_DATA);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  const clearChannelFault = async (channelNumber: number) => {
    setClearingChannel(channelNumber);

    try {
      await changeChannelAction(id, channelNumber, ChangeChannelAction.RESET_CHANNEL_FAULTS, '', false);
      await fetchData();
    } finally {
      setClearingChannel(null);
    }
  };

  return (
    <div className="p-edit-controller-channel-faults pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">Channel Faults</h3>
        </div>

        {loading ? (
          <div className="flex h-full w-full items-center justify-center py-10">
            <SpinnerLoader />
          </div>
        ) : faultRows.length === 0 ? (
          <div className="flex h-full w-full flex-col gap-3.5 p-10">
            <EmptyDataTable />
          </div>
        ) : (
          <ul className="c-channel-faults-list flex flex-col divide-y divide-stroke dark:divide-strokedark">
            {faultRows.map((row, index) => (
              <li
                key={`${row.channelNumber}-${index}`}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-7 py-3 text-sm text-black dark:text-white"
              >
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="font-semibold whitespace-nowrap">Channel {row.channelNumber}</span>
                  <span className="whitespace-nowrap text-body dark:text-bodydark">{row.channelName}</span>
                  <span>{row.message}</span>
                </div>
                {canClearFaults && (
                  <InteractiveButton
                    size={ElementSize.SM}
                    type={ButtonType.PrimaryInverse}
                    text={clearingChannel === row.channelNumber ? 'Clearing...' : 'Clear Fault'}
                    disabled={clearingChannel !== null}
                    onClick={() => clearChannelFault(row.channelNumber)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannels: dispatch.channels.getChannels,
  getListChannelFaults: dispatch.channels.getListChannelFaults,
});

export default connect(mapState, mapDispatch)(ChannelFaults);
